import { createContext, useContext, useState, useCallback } from 'react';

// Create the context
const ClassContext = createContext();

// Custom hook to use the class context
export const useClass = () => {
  const context = useContext(ClassContext);
  if (!context) {
    throw new Error('useClass must be used within a ClassProvider');
  }
  return context;
};

// Class provider component
export const ClassProvider = ({ children }) => {
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPublicView, setIsPublicView] = useState(false);
  const [publicClassId, setPublicClassId] = useState(null);

  // NOTE: We do NOT persist selectedClass to localStorage
  // The selected class is ephemeral and should be cleared on page refresh
  // Users should select their class from the Dashboard on each session

  // Function to change the selected class - memoized to prevent infinite loops
  const changeClass = useCallback((classData, isPublic = false, publicId = null) => {
    setSelectedClass(classData);
    setIsPublicView(isPublic);
    setPublicClassId(publicId);
  }, []);

  // Function to clear the selected class
  const clearClass = () => {
    setSelectedClass(null);
    setIsPublicView(false);
    setPublicClassId(null);
  };

  // Function to update selected class data
  const updateClassData = (updates) => {
    if (selectedClass) {
      setSelectedClass(prev => ({
        ...prev,
        ...updates
      }));
    }
  };

  // Function to check if a class is selected
  const hasClassSelected = () => {
    return selectedClass && selectedClass.class_id && selectedClass.class_id !== 'all';
  };

  // Function to get class context for API calls - memoized to prevent unnecessary re-renders
  const getClassContext = useCallback(() => {
    if (isPublicView && publicClassId) {
      return {
        class_id: publicClassId,
        is_public_view: true
      };
    }
    if (!selectedClass || selectedClass.class_id === 'all') {
      return null;
    }
    return {
      class_id: selectedClass.class_id,
      class_code: selectedClass.class_code
    };
  }, [isPublicView, publicClassId, selectedClass]);

  // Function to get display name for selected class
  const getClassDisplayName = () => {
    if (!selectedClass) return 'No class selected';
    if (selectedClass.class_id === 'all') return 'All Classes';
    return `${selectedClass.class_name} (${selectedClass.class_code})`;
  };

  // Function to check if user should be redirected to Dashboard
  const shouldRedirectToDashboard = (currentPage) => {
    // Pages that don't require class selection
    const publicPages = ['dashboard', 'login', 'consent', 'api-key-form'];
    const globalPages = ['settings', 'help', 'about'];
    
    // If it's a public or global page, don't redirect
    if (publicPages.includes(currentPage) || globalPages.includes(currentPage)) {
      return false;
    }
    
    // If in public view, don't redirect
    if (isPublicView) {
      return false;
    }
    
    // If no class is selected for class-specific pages, redirect to dashboard
    return !hasClassSelected();
  };

  // Function to determine if class sidebar should be visible
  const shouldShowClassSidebar = (currentPage) => {
    const pagesWithoutClassSidebar = ['dashboard', 'settings', 'help', 'about'];
    return hasClassSelected() && !pagesWithoutClassSidebar.includes(currentPage);
  };

  const value = {
    selectedClass,
    loading,
    setLoading,
    changeClass,
    clearClass,
    updateClassData,
    hasClassSelected,
    getClassContext,
    getClassDisplayName,
    shouldRedirectToDashboard,
    shouldShowClassSidebar,
    isPublicView,
    publicClassId
  };

  return (
    <ClassContext.Provider value={value}>
      {children}
    </ClassContext.Provider>
  );
};

export default ClassContext;